import {
  base_colorAction,
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_colorSurfaceSecondary,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_family_weight_semibold,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Applied to `BaseFlyout` itself (the class lands on the portal, so descendant
 * selectors reach the panel). BaseFlyout's body defaults to the recessed page
 * surface; the design puts these drawers on the raised white surface, matching
 * what `TransactionDetail` already does for its own flyout.
 */
export const flyoutStyle = css`
  &.SpendRestrictionsDrawer {
    .BaseFlyout-Content {
      background-color: ${base_colorRaisedSurface};

      /*
       * The deliverables put both of these drawers on the shared Flyout at a
       * fixed 640px, and call that width out as *not* an implementer's choice.
       * BaseFlyout's own preferredWidth is spacing(145) = 580px and is shared by
       * every other flyout, so widen here rather than moving the primitive.
       */
      width: min(${spacing(160)}, 100vw);

      .BaseFlyout-CloseBar,
      .BaseFlyout-Body {
        background-color: ${base_colorRaisedSurface};
      }
    }
  }
`

export default css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};

  .SpendRestrictionsTile-intro {
    margin: 0 0 ${spacing(2)};
    max-width: 60ch;
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-body {
    margin: 0 0 ${spacing(3)};
    max-width: 56ch;
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-summaryText {
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-summaryPills {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${spacing(1)};
  }

  /* ---- drawer ---- */

  /*
   * Content-owned heading. BaseFlyout's own \`title\`/\`subtitle\` render a raised
   * header band with a 32px h2; the design is a single flat surface with a 20px
   * heading, so the drawers pass neither prop and style the block here.
   */
  .SpendRestrictionsTile-drawerHeading h2 {
    margin: 0 0 6px;
    font-family: ${font_family_weight_medium};
    ${font_2xl};
    color: ${text_colorTextPrimary};
  }

  .SpendRestrictionsTile-drawerIntro {
    margin: 0 0 ${spacing(1)};
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-drawerLead {
    margin: 0;
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-drawerSection {
    border-top: 1px solid ${base_colorContentBorder};
    margin-top: ${spacing(4)};
    padding-top: ${spacing(4)};
  }

  /*
   * There is deliberately no :first-child reset here. The heading block is
   * always the drawer's first child, so such a rule could never match again —
   * and the divider it would have suppressed is wanted: browser-checked in both
   * drawers, the first section reads as cramped against the heading without it.
   * Dropping the dead rule rather than reviving it keeps that intentional.
   */

  .SpendRestrictionsTile-drawerKicker {
    margin-bottom: ${spacing(3)};
    font-family: ${font_family_weight_semibold};
    font-size: 11.5px;
    line-height: 16px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${text_colorTextSecondary};
  }

  /* The exception list sits tighter under its kicker than the other sections. */
  .SpendRestrictionsTile-drawerKicker.forList {
    margin-bottom: ${spacing(1)};
  }

  .SpendRestrictionsTile-addList,
  .SpendRestrictionsTile-exceptionList {
    list-style: none;
    margin: ${spacing(2)} 0 0;
    padding: 0;
  }

  .SpendRestrictionsTile-addList {
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${spacing(2)};
    overflow: hidden;
  }

  .SpendRestrictionsTile-addItem {
    border-bottom: 1px solid ${base_colorContentBorder};
  }

  .SpendRestrictionsTile-addItem:last-child {
    border-bottom: none;
  }

  .SpendRestrictionsTile-addCodes:not(:empty) {
    padding: 0 ${spacing(3)} ${spacing(2)};
  }

  .SpendRestrictionsTile-addRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: ${spacing(2)} ${spacing(3)};
    border: none;
    background: none;
    cursor: pointer;
    font-family: ${font_family_weight_regular};
    ${font_md};
    color: ${text_colorTextPrimary};
  }

  .SpendRestrictionsTile-addRow:hover {
    background-color: ${base_colorSurfaceSecondary};
  }

  .SpendRestrictionsTile-addRow > span:last-child {
    color: ${base_colorAction};
    font-family: ${font_family_weight_medium};
  }

  .SpendRestrictionsTile-addRow:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: -2px;
  }

  /* Closing info note, mirroring the design's drawer footer. The glyph is
     decorative — the sentence beside it carries the meaning. */
  .SpendRestrictionsTile-drawerNote {
    display: flex;
    align-items: flex-start;
    gap: ${spacing(2)};
    margin: ${spacing(4)} 0 0;
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-drawerNote > svg {
    flex: 0 0 auto;
    margin-top: ${spacing(0.25)};
  }

  /* SvgTooltip defaults its fill to the CTA colour, which made this decorative
     glyph read as a tappable affordance next to plain text. It is NOT a
     tooltip — the sentence beside it is always visible — so it inherits the
     note's own secondary grey, matching the design's currentColor glyph.
     A presentation attribute loses to any CSS rule, so this wins over the
     inline fill. */
  .SpendRestrictionsTile-drawerNote > svg g {
    fill: currentColor;
  }

  .SpendRestrictionsTile-addEmpty,
  .SpendRestrictionsTile-drawerEmpty {
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  /* Sits INSIDE the bordered result list, so it takes the same inset as a
     result row. With the row's horizontal padding missing, "Loading" and
     "No matches" hugged the list's left border while every real result was
     indented past it. The list already carries the top margin. */
  .SpendRestrictionsTile-addEmpty {
    margin: 0;
    padding: ${spacing(2)} ${spacing(3)};
  }

  .SpendRestrictionsTile-drawerEmpty {
    margin: ${spacing(2)} 0 0;
    padding: ${spacing(2)} 0;
  }

  .SpendRestrictionsTile-exceptionRow {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${spacing(3)};
    padding: ${spacing(3)} 0;
    border-bottom: 1px solid ${base_colorContentBorder};
  }

  .SpendRestrictionsTile-exceptionRow:last-child {
    border-bottom: none;
  }

  /* The geofence drawer keeps the hairline under its LAST country, per the
     design — it closes the list off against the note below it. Scoped, because
     the MCC drawer shares this row style and ends its list flush. */
  .SpendRestrictionsTile-exceptionList.isGeofence
    .SpendRestrictionsTile-exceptionRow:last-child {
    border-bottom: 1px solid ${base_colorContentBorder};
  }

  .SpendRestrictionsTile-exceptionMain {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .SpendRestrictionsTile-exceptionName {
    font-family: ${font_family_weight_medium};
    ${font_md};
    color: ${text_colorTextPrimary};
  }

  .SpendRestrictionsTile-exceptionControl {
    display: inline-flex;
    align-items: center;
    gap: ${spacing(2)};
    flex: 0 0 auto;
  }

  .SpendRestrictionsTile-removeButton {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0 ${spacing(1)};
    font-size: 18px;
    line-height: 1;
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-removeButton:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
    border-radius: ${spacing(1)};
  }

  /* ---- MCC code expander ---- */
  .SpendRestrictionsTile-codesToggle {
    display: inline-flex;
    align-items: center;
    border: none;
    background: none;
    cursor: pointer;
    padding: ${spacing(1)} 0;
    font-family: ${font_family_weight_medium};
    ${font_sm};
    color: ${base_colorAction};
  }

  .SpendRestrictionsTile-codesToggle:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .SpendRestrictionsTile-codesToggle:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
    border-radius: ${spacing(1)};
  }

  .SpendRestrictionsTile-codeList {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    margin: ${spacing(1)} 0;
    padding: 0 0 0 ${spacing(3)};
  }

  .SpendRestrictionsTile-codeRow {
    display: flex;
    gap: ${spacing(2)};
  }

  .SpendRestrictionsTile-code {
    flex: 0 0 auto;
    min-width: 68px;
    font-family: ui-monospace, Menlo, monospace;
    font-variant-numeric: tabular-nums;
    ${font_sm};
    color: ${text_colorTextPrimary};
  }

  .SpendRestrictionsTile-codeLabel {
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  /* ---- drawer footer ---- */
  .SpendRestrictionsTile-drawerFooter {
    display: flex;
    align-items: flex-start;
    gap: ${spacing(2)};
    border-top: 1px solid ${base_colorContentBorder};
    margin-top: ${spacing(4)};
    padding-top: ${spacing(3)};
    font-family: ${font_family_weight_regular};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .SpendRestrictionsTile-drawerFooter p {
    margin: 0;
  }

  .SpendRestrictionsTile-drawerActions {
    display: flex;
    justify-content: flex-end;
    gap: ${spacing(2)};
    border-top: 1px solid ${base_colorContentBorder};
    margin-top: ${spacing(4)};
    padding-top: ${spacing(3)};
  }

  /* Sized to the footer's ${font_sm} text rather than the icon's native 24px. */
  .SpendRestrictionsTile-drawerFooterIcon {
    flex: 0 0 auto;
    width: ${spacing(4)};
    height: ${spacing(4)};
  }
`
